import React, {createContext, useContext, useState} from 'react';
import {ApplicationString as EnglishStrings} from './Locales/en';
import {ApplicationString as JapaneseStrings} from './Locales/ja';

// Create the context
const LanguageContext = createContext();

// Language configuration
export const LANGUAGES = {
  EN: 'en',
  JA: 'ja',
};

export const LANGUAGE_NAMES = {
  [LANGUAGES.EN]: 'English',
  [LANGUAGES.JA]: '日本語',
};

// Language strings mapping
const languageStrings = {
  [LANGUAGES.EN]: EnglishStrings,
  [LANGUAGES.JA]: JapaneseStrings,
};

// Language Provider Component
export const LanguageProvider = ({children}) => {
  const [currentLanguage, setCurrentLanguage] = useState(LANGUAGES.EN);

  // Initialize language from localStorage if available
  React.useEffect(() => {
    const savedLanguage = localStorage.getItem('pbssLanguage');
    if (savedLanguage && Object.values(LANGUAGES).includes(savedLanguage)) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  // Function to change language
  const changeLanguage = (languageCode) => {
    if (Object.values(LANGUAGES).includes(languageCode)) {
      setCurrentLanguage(languageCode);
      if (languageCode === LANGUAGES.JA) {
        localStorage.setItem('pbssLanguage', languageCode);
      }
    }
  };

  // Function to get localized string
  const getString = (key, fallback = 'No string found') => {
    const strings = languageStrings[currentLanguage];
    if (!strings) {
      return;
    }
    return strings[key] || fallback;
  };

  // Function to get all strings for current language
  const getStrings = () => {
    return languageStrings[currentLanguage];
  };

  // Check if current language is RTL (for future use)
  const isRTL = () => {
    // Add RTL languages here if needed in the future
    return false;
  };

  const contextValue = {
    currentLanguage,
    changeLanguage,
    getString,
    getStrings,
    isRTL,
    availableLanguages: LANGUAGES,
    languageNames: LANGUAGE_NAMES,
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use the language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Export the context for direct access if needed
export default LanguageContext;
